/* eslint-disable react/display-name */
import { type ComponentType, memo, useState } from 'react';

import { Combobox } from '../../Form';

import type { ChangeComponentProps } from './simpleFieldWrappers';

interface CatalogItem {
  id?: string | number;
  name?: string;
}

interface UseCatalogListResult<TCatalogItem extends CatalogItem = CatalogItem> {
  data?: Array<TCatalogItem>;
  loading?: boolean;
  error?: unknown;
}

type UseCatalogList<TCatalogItem extends CatalogItem = CatalogItem> = (
  catalogName: string
) => [UseCatalogListResult<TCatalogItem>, ...Array<unknown>];

type ChangeComponentType = ComponentType<ChangeComponentProps>;

export interface ListFieldWrapperProps {
  label: string;
  name: string;
  description?: string;
  readOnly?: boolean;
  isFormula: boolean;
  nameCatalog: string;
  ChangeComponent?: ChangeComponentType;
  useCatalogList: UseCatalogList;
}

interface MemoProps extends ListFieldWrapperProps {
  onBlur: (value: CatalogItem | null) => void;
}

const ListMemoComponent = memo(
  ({ readOnly, label, name, description, onBlur, nameCatalog, useCatalogList }: MemoProps) => {
    const [{ data, loading, error }] = useCatalogList(nameCatalog);

    if (loading || error) {
      return <></>;
    }

    return (
      <Combobox
        readOnly={readOnly}
        label={label}
        name={name}
        description={description}
        data={data}
        display="name"
        code="id"
        whenChange={(data) => {
          onBlur(data);
        }}
      />
    );
  }
);

export const ListFieldWrapper = ({
  readOnly,
  label,
  name,
  description,
  isFormula,
  nameCatalog,
  ChangeComponent,
  useCatalogList
}: ListFieldWrapperProps) => {
  const [cellValue, setCellValue] = useState<CatalogItem | null | undefined>(undefined);

  return (
    <>
      {!isFormula && ChangeComponent ? <ChangeComponent value={cellValue} /> : null}
      <ListMemoComponent
        nameCatalog={nameCatalog}
        onBlur={setCellValue}
        readOnly={readOnly}
        label={label}
        name={name}
        description={description}
        isFormula={isFormula}
        ChangeComponent={ChangeComponent}
        useCatalogList={useCatalogList}
      />
    </>
  );
};
